import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { nationality_data } from "./all";
import { RiArrowRightDoubleLine } from "react-icons/ri";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";

import { toEthiopian, toGregorian } from "ethiopian-date";
import { correctionThunks } from "../../Redux/thunks/correctionThunks";
import "react-datepicker/dist/react-datepicker.css";
import DateConverter from "./DateConverter";
import DisplayProfileCard from "./components/displayCard";
import EditCard from "./components/editCard";
import UploadDocument from "./components/uploadDocument";
import DocumentPreview from "./components/documentPreview";
import NavBar from "../../Components/Nav/newNavBar";
import { useFormik } from "formik";
import { formSchema } from "./formSchema";
import useCorrectDemoLayoutForm from "./correctDemoLayoutForm";
import ConfirmModal from "./components/confirmModal";
import NotificationModal from "../../Components/NotificationModal";
import ContactOtpModal from "../../Components/ContactOtpModal/ContactOtpModal";
import { buildContactOtpSteps } from "../../utils/contactOtp";
import { deepEqual } from "../../hooks/deepCheck";
import Footer from "../../Components/Footer/footer";
import { useNavigate } from "react-router-dom";


const CorrectDemo = () => {
    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        return `${year}/${month}/${day}`;
    };

    const formattedEthiopianDate = (gregorianDate) => {
        const formattedDate = gregorianDate.replace(/\//g, "-") || "";
        const [year, month, day] = formattedDate.split("-").map(Number);
        const ethiopian = toEthiopian(year, month, day);
        const formattedEthiopianDate = `${String(ethiopian[0]).padStart(
            2,
            "0"
        )}/${String(ethiopian[1]).padStart(2, "0")}/${String(
            ethiopian[2]
        ).padStart(2, "0")}`;
        return formattedEthiopianDate;
    };

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { data, isLoading, error } = useSelector((state) => state.update);
    const [openNotificationModal, setOpenNotificationModal] = useState(false);
    const [userError, setUserError] = useState("");

    useEffect(() => {
        if (error || data) {
            setOpenNotificationModal(true);
            setOpenModal(false);
            setContactOtpOpen(false);
            setPendingFormData(null);
        }
    }, [error, data]);

    useEffect(() => {
        setOpenNotificationModal(false);
        setUserError("");
        setOpenModal(false);
    }, []);

    const uniqueIdObject = useSelector((state) => state.otp.data);
    const [rateLimiterCount, setRateLimiterCount] = useState(0);
    const fileInputRef = useRef(null);
    const toastId = useRef(null);

    // Location is display-only on correction — show the resident's current
    // region/zone/woreda exactly as returned by the backend identity DTO.
    const initialState = {
        fullnameEng: uniqueIdObject?.fullName?.eng.trim() || "",
        fullnameAmh: uniqueIdObject?.fullName?.amh.trim() || "",
        gender: uniqueIdObject?.gender?.eng || "",
        region: uniqueIdObject?.region?.eng || "",
        zone: uniqueIdObject?.zone?.eng || "",
        woreda: uniqueIdObject?.woreda?.eng || "",
        nationality:
            nationality_data?.find(
                (obj) => obj.name === uniqueIdObject?.residenceStatus?.eng
            )?.id || "57",
        dateOfBirth: formatDate(uniqueIdObject?.dateOfBirth),
        phone: uniqueIdObject?.phone || "",
        email: uniqueIdObject?.email || "",
        uin: uniqueIdObject?.uin || "",
        signature: uniqueIdObject?.signature || "",
        proofOfAddress: uniqueIdObject?.proofOfAddress || "",
        individualBiometrics: uniqueIdObject?.individualBiometrics || "",
        proofOfIdentity: uniqueIdObject?.proofOfIdentity || "",
        idschemaVersion: uniqueIdObject?.idschemaVersion || "",
        documentOne:
            uniqueIdObject?.documents?.find(
                (obj) => obj?.category === "proofOfAddress"
            ) || "",
        documentTwo:
            uniqueIdObject?.documents?.find(
                (obj) => obj?.category === "proofOfIdentity"
            ) || "",
        documentThree:
            uniqueIdObject?.documents?.find(
                (obj) => obj?.category === "CONS"
            ) || "",
        photo: uniqueIdObject?.photo || "",
        uploadProof: "",
        dobAmh: formattedEthiopianDate(uniqueIdObject?.dateOfBirth),
        uploadDocumentType: "",
    };

    const submitter = async (data) => {
        if (rateLimiterCount <= 3) {
            setRateLimiterCount((prevCount) => prevCount + 1);
        }
        setUserError("");

        if (deepEqual(initialState, data)) {
            setUserError("You have not made any changes.");
            setOpenNotificationModal(true);
            setOpenModal(false);
            return;
        }

        const steps = buildContactOtpSteps(initialState, data);
        if (steps.length > 0) {
            setPendingFormData(data);
            setContactOtpSteps(steps);
            setContactOtpOpen(true);
            setOpenModal(false);
            return;
        }

        doSubmit(data);
    };

    const doSubmit = (data) => {
        const changedFields = Object.keys(initialState).filter(
            (key) => initialState[key] !== data[key]
        );

        const isImmediateUpdate =
            changedFields.length > 0 &&
            changedFields.every((field) => field === "phone" || field === "email");

        const phoneChanged = initialState.phone !== data.phone;
        const emailChanged = initialState.email !== data.email;

        const updatedDTO = {
            fullName: {
                eng: data.fullnameEng.trim(),
                amh: data.fullnameAmh.trim(),
            },
            gender: {
                eng: data.gender === "Male" ? "Male" : "Female",
                amh: data.gender === "Male" ? "ወንድ" : "ሴት",
            },
            dateOfBirth: data.dateOfBirth,
            residenceStatus: {
                eng: nationality_data.find(
                    (item) => item.id === Number(data.nationality)
                ).name,
                amh: nationality_data.find(
                    (item) => item.id === Number(data.nationality)
                ).nameamh,
            },
            region: {
                eng: uniqueIdObject?.region?.eng,
                amh: uniqueIdObject?.region?.amh,
            },
            zone: {
                eng: uniqueIdObject?.zone?.eng,
                amh: uniqueIdObject?.zone?.amh,
            },
            woreda: {
                eng: uniqueIdObject?.woreda?.eng,
                amh: uniqueIdObject?.woreda?.amh,
            },
            phone: data?.phone?.replace(/^(\+251|251)/, "0").trim() || "",
            email: data.email,
            uin: data.uin,
            signature: data.signature,
            proofOfAddress: {
                format: "PDF",
                type: "DOC005",
                value: "proofOfAddress",
            },
            individualBiometrics: {
                format: "cbeff",
                version: 1,
                value: "individualBiometrics_bio_CBEFF",
            },
            proofOfIdentity: {
                format: "PDF",
                type: "DOC005",
                value: "proofOfIdentity",
            },
            idschemaVersion: 1,
            documentOne: data.documentOne,
            documentTwo: data.documentTwo,
            documentThree: data.documentThree,
            photo: data.photo,
            isImmediateUpdate,
            ...(phoneChanged && { phoneOtpVerified: true }),
            ...(emailChanged && { emailOtpVerified: true }),
        };

        dispatch(
            correctionThunks(
                updatedDTO,
                uniqueIdObject?.uin,
                "FIN",
                data.signature,
                uniqueIdObject.regId
            )
        );
    };

    const handleContactOtpVerified = () => {
        if (pendingFormData) {
            doSubmit(pendingFormData);
        }
    };

    if (rateLimiterCount >= 3) {
        setTimeout(() => {
            setRateLimiterCount(0);
        }, 10000);
    }

    const {
        values,
        touched,
        errors,
        dirty,
        setFieldValue,
        handleChange,
        handleSubmit,
        handleBlur,
    } = useFormik({
        initialValues: initialState,
        enableReinitialize: true,
        validationSchema: formSchema,
        onSubmit: submitter,
    });

    const { layout } = useCorrectDemoLayoutForm({
        identity: uniqueIdObject,
    });
    const [openModal, setOpenModal] = useState(false);
    const [contactOtpOpen, setContactOtpOpen] = useState(false);
    const [contactOtpSteps, setContactOtpSteps] = useState([]);
    const [pendingFormData, setPendingFormData] = useState(null);

    return (
        <div className='relative bg-[whitesmoke] z-0 flex flex-col items-center justify-center w-full min-h-screen bg-bottom bg-cover bg-opacity-6 font-lato'>
            <NavBar />
            <div className='max-w-[1400px] mx-auto px-5 mt-20 md:mt-40 mb-5'>
                <p className='w-full text-MetallicBlue font-semibold text-3xl'>
                    {t("correct-demo")}
                </p>
            </div>
            <form
                onSubmit={handleSubmit}
                className='max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 w-full gap-5 text-MetallicBlue px-5 mb-20'
            >
                {layout.map((layoutItem, index) =>
                    layoutItem.type === "document" ? (
                        <UploadDocument
                            key={index}
                            title={layoutItem.title}
                            values={values}
                            errors={errors}
                            fields={layoutItem.formFields}
                            handleBlur={handleBlur}
                            touched={touched}
                            setFieldValue={setFieldValue}
                            fileInputRef={fileInputRef}
                        />
                    ) : layoutItem.type === "edit" ? (
                        <EditCard
                            key={index}
                            changeHandler={handleChange}
                            setFieldValue={setFieldValue}
                            values={values}
                            errors={errors}
                            title={layoutItem.title}
                            fields={layoutItem.formFields}
                            handleBlur={handleBlur}
                            touched={touched}
                        />
                    ) : layoutItem.type === "preview" ? (
                        <DocumentPreview
                            key={index}
                            title={layoutItem.title}
                            data={[
                                {
                                    doc: values?.documentOne?.value,
                                    title: "Proof of Address",
                                },
                                {
                                    doc: values?.documentTwo?.value,
                                    title: "Proof of Identity",
                                },
                                {
                                    doc: values?.documentThree?.value,
                                    title: "Consent Form",
                                },
                            ]}
                        />
                    ) : (
                        <DisplayProfileCard
                            key={index}
                            name={uniqueIdObject?.fullName || ""}
                            fin={initialState.uin}
                            photo={initialState.photo}
                            verified={true}
                        />
                    )
                )}
                <div className='order-first md:col-span-3 py-5 px-7 flex flex-col w-full gap-3 bg-gray-200 leading-5'>
                    <p className='font-bold text-2xl'>{t("instructions")}</p>
                    <p>{t("correction-instructions-title")}</p>
                    <p className="text-red-600 font-semibold">{t("correction-instructions-notice")}</p>
                    <p>{t("correction-instructions-description1")}</p>
                    <p>{t("correction-instructions-description2")}</p>
                    <p>{t("correction-instructions-description3")}</p>
                    <p>{t("instructions-help")}</p>
                </div>
                <div className='w-full flex justify-center items-center bg-gray-200 shadow-sm py-10'>
                    <button
                        type='button'
                        onClick={() => setOpenModal(!openModal)}
                        className={` ${!dirty ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} bg-MetallicBlue items-center flex justify-center gap-3 text-white py-4 px-8 rounded-full text-xl`}
                        disabled={!dirty || isLoading}
                    >
                        <p>{t("submit-changes")}</p>
                        <RiArrowRightDoubleLine size={30} />
                    </button>
                </div>
                <ConfirmModal
                    open={openModal}
                    handleClose={() => setOpenModal(false)}
                    submit={handleSubmit}
                    loading={isLoading}
                />
                <ContactOtpModal
                    open={contactOtpOpen}
                    steps={contactOtpSteps}
                    onVerified={handleContactOtpVerified}
                    submitting={isLoading}
                    onClose={() => {
                        if (isLoading) return;
                        setContactOtpOpen(false);
                        setPendingFormData(null);
                    }}
                />
                {!isLoading ? (
                    <NotificationModal
                        open={openNotificationModal}
                        error={error || userError ? true : false}
                        title={error || userError ? "Error" : "Success"}
                        message={
                            userError
                                ? userError
                                : error
                                ? error
                                : data?.response === "OK"
                                ? data?.response
                                : null
                        }
                        handleClose={() => {
                            setOpenNotificationModal(false);
                            navigate("/dashboard");
                        }}                    />
                ) : null}
            </form>
            <Footer />
        </div>
    );
};

export default CorrectDemo;